import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { usersAPI } from '../services/api.ts';
import { BellIcon, ClockIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

const Reminders: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    email_reminders_enabled: false,
    reminder_time: '09:00',
    reminder_timezone: 'Europe/London'
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Time options (every hour from 6 AM to 10 PM)
  const timeOptions = Array.from({ length: 17 }, (_, i) => {
    const hour = i + 6;
    const timeString = `${hour.toString().padStart(2, '0')}:00`;
    return { value: timeString, label: `${hour}:00 ${hour < 12 ? 'AM' : hour === 12 ? 'PM' : 'PM'}` };
  });

  // Common timezones
  const timezoneOptions = [
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)' },
    { value: 'America/New_York', label: 'New York (EST/EDT)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' }
  ];

  useEffect(() => {
    fetchReminderSettings();
  }, []);

  const fetchReminderSettings = async () => {
    try {
      const response = await usersAPI.getReminderSettings();
      setSettings({
        email_reminders_enabled: response.data.email_reminders_enabled || false,
        reminder_time: response.data.reminder_time || '09:00',
        reminder_timezone: response.data.reminder_timezone || 'Europe/London'
      });
    } catch (error) {
      console.error('Error fetching reminder settings:', error);
      setMessage({ type: 'error', text: 'Failed to load reminder settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      await usersAPI.updateReminderSettings(settings);
      setMessage({ type: 'success', text: 'Reminder settings saved successfully!' });
    } catch (error: any) {
      console.error('Error saving reminder settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to save reminder settings' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reminder settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <BellIcon className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Email Reminders</h1>
          </div>
          <p className="mt-2 text-gray-600">
            Configure daily email reminders for your equipment hires and returns.
          </p>
        </div>

        {/* Settings Card */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-8">
            {/* Enable/Disable Toggle */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Email Reminders</h3>
                  <p className="text-sm text-gray-500">
                    Receive daily digest emails about your equipment hires
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.email_reminders_enabled}
                    onChange={(e) => handleSettingChange('email_reminders_enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            {/* Time and Timezone Settings */}
            {settings.email_reminders_enabled && (
              <div className="space-y-6">
                {/* Reminder Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <ClockIcon className="h-4 w-4 inline mr-1" />
                    Reminder Time
                  </label>
                  <select
                    value={settings.reminder_time}
                    onChange={(e) => handleSettingChange('reminder_time', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    {timeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose when you'd like to receive your daily reminder email
                  </p>
                </div>

                {/* Timezone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                    Timezone
                  </label>
                  <select
                    value={settings.reminder_timezone}
                    onChange={(e) => handleSettingChange('reminder_timezone', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    {timezoneOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Select your timezone for accurate reminder timing
                  </p>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </button>
            </div>

            {/* Message */}
            {message && (
              <div className={`mt-4 p-4 rounded-md ${
                message.type === 'success' 
                  ? 'bg-green-50 border border-green-200 text-green-800' 
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                {message.text}
              </div>
            )}
          </div>
        </div>

        {/* Information Card */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-2">What's included in reminder emails?</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Active Hires:</strong> Equipment currently on hire at each site</li>
            <li>• <strong>Upcoming Returns:</strong> Equipment due back in the next 1-3 days</li>
            <li>• <strong>Overdue Returns:</strong> Equipment past its return date</li>
            <li>• <strong>Site Summary:</strong> Organized by site location for easy reference</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Reminders;
